// =========================================================================
// LineChart — a hand-rolled SVG line/area chart. Fork-owned (zone C).
//
// Port of `lineChart(id, series, opts)` at line 1965 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`. The demo wrote
// an SVG string into a container by id; this renders the same geometry as JSX.
//
// The maths is verbatim: the same padding box (52/16/14/42), the same
// `Math.floor(min - 1)` / `Math.ceil(max + 1)` domain, the same four y ticks,
// the same `ceil(n / xticks)` label stride, the same dashed threshold and
// event rules, the same 0.07 area fill opacity and 2.2px dots.
//
// Two deliberate differences from the demo, both documented at the call site
// that needs them:
//
// 1. **Null points break the line.** The demo dropped nulls out of the point
//    list and let the polyline bridge the gap. Every incident series in
//    `seed/incidents.ts` ends on a null final day (the reading was never
//    taken), so on this data the two behave identically — the line stops one
//    day early either way. Breaking is the honest rendering and is what a
//    mid-series gap would need, so it is what this does.
// 2. **Colours are CSS custom properties passed in by the caller.** The demo
//    interpolated `var(--warn)` into the string; here the token travels as a
//    prop so nothing in this file hard-codes a colour. The demo's
//    `--line` / `--line-2` / `--muted` / `--ink-2` map onto the foundation's
//    `--hairline-strong` / `--hairline` / `--ink-tertiary` / `--ink-secondary`.
//
// `viewBox` is fixed (520 × 210 by default) so parity snapshots stay
// byte-identical; the SVG scales with its container.
//
// UI layer — renders output, no data access.
// =========================================================================

/** One plotted series. `null` values break the line rather than reading zero. */
export type LineSeries = {
  readonly values: readonly (number | null)[];
  /** Stroke colour as a CSS custom property, e.g. `'var(--warn)'`. */
  readonly color: string;
  /** Fill the area under the line at 7% opacity. */
  readonly area?: boolean;
  /** Draw a 2.2px dot at every non-null point. */
  readonly dots?: boolean;
  /** Stroke width; the demo's default is 1.8. */
  readonly width?: number;
  /** Legend label. The legend renders only when there is more than one series. */
  readonly name?: string;
};

/** Semantic colour for a reference line or marker; resolves to a theme token. */
export type LineTone = 'crit' | 'warn' | 'info' | 'good' | 'muted' | 'ink';

/** A labelled horizontal reference line — a control limit, a centre line, a spec limit. */
export type LineHLine = {
  readonly value: number;
  readonly label?: string;
  /** Default `'crit'`. */
  readonly tone?: LineTone;
  /** Dashed (default) or solid. */
  readonly dashed?: boolean;
  /** Which end of the plot the label sits at. Default `'end'`. */
  readonly align?: 'start' | 'end';
};

/** A highlighted point on a series — a run-rule signal, an event. */
export type LineMarker = {
  /** Point index on the label axis. */
  readonly index: number;
  /** Short text drawn above the point, e.g. `'R1'`. */
  readonly label?: string;
  /** Default `'crit'`. */
  readonly tone?: LineTone;
  /** Which series the point sits on. Default 0. */
  readonly seriesIndex?: number;
  /** Accessible / hover description. */
  readonly title?: string;
};

const TONE: Readonly<Record<LineTone, string>> = {
  crit: 'var(--crit)',
  warn: 'var(--warn)',
  info: 'var(--info)',
  good: 'var(--good)',
  muted: 'var(--ink-tertiary)',
  ink: 'var(--ink-secondary)',
};

export type LineChartProps = {
  /** X-axis labels, one per point. Length sets the number of points. */
  readonly labels: readonly string[];
  readonly series: readonly LineSeries[];
  /** Forced y-axis minimum; defaults to `floor(min(values) - 1)`. */
  readonly min?: number;
  /** Forced y-axis maximum; defaults to `ceil(max(values) + 1)`. */
  readonly max?: number;
  /** Horizontal gridlines / y labels. The demo's default is 4. */
  readonly ticks?: number;
  /** Target number of x labels; the stride is `ceil(n / xticks)`. Default 8. */
  readonly xticks?: number;
  /** Decimal places on the y labels. Default 0. */
  readonly dec?: number;
  /** Threshold value — drawn as a dashed critical rule. */
  readonly hline?: number;
  readonly hlabel?: string;
  /** Index of an event day — drawn as a dashed vertical critical rule. */
  readonly vline?: number;
  readonly vlabel?: string;
  /** Additional labelled reference lines, drawn under the series. Additive to `hline`. */
  readonly hlines?: readonly LineHLine[];
  /** Highlighted points, drawn over the series. */
  readonly markers?: readonly LineMarker[];
  readonly xTitle?: string;
  readonly yTitle?: string;
  /** Accessible name; falls back to `yTitle`. */
  readonly ariaLabel?: string;
  readonly width?: number;
  readonly height?: number;
  /** Custom y-label formatter; overrides `dec`. */
  readonly format?: (value: number) => string;
  readonly className?: string;
};

/** The demo's padding box — left, right, top, bottom. */
const PAD = { left: 52, right: 16, top: 14, bottom: 42 } as const;

/** Grid and axis hairlines, mapped from the demo's `--line-2` / `--line`. */
const GRID = 'var(--hairline)';
const AXIS = 'var(--hairline-strong)';
const LABEL = 'var(--ink-tertiary)';
const TITLE = 'var(--ink-secondary)';
/** Label halo — the page surface, so a label stays legible where it crosses a line. */
const HALO = 'var(--surface-1)';
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const SANS = 'inherit';

/**
 * Split a series into runs of consecutive non-null points.
 *
 * @returns one entry per unbroken run, each carrying the original point index
 *   so the x scale stays anchored to the label axis.
 */
function segments(
  values: readonly (number | null)[],
): { index: number; value: number }[][] {
  const runs: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  values.forEach((value, index) => {
    if (value == null) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }
    current.push({ index, value });
  });
  if (current.length) runs.push(current);
  return runs;
}

export function LineChart({
  labels,
  series,
  min,
  max,
  ticks = 4,
  xticks = 8,
  dec = 0,
  hline,
  hlabel,
  vline,
  vlabel,
  hlines,
  markers,
  xTitle,
  yTitle,
  ariaLabel,
  width = 520,
  height = 210,
  format,
  className,
}: LineChartProps) {
  const n = labels.length;
  const finite = series
    .flatMap((s) => s.values)
    .filter((v): v is number => v != null)
    .concat(hline != null ? [hline] : [])
    .concat(hlines ? hlines.map((h) => h.value) : []);

  // An empty series would make `Math.min(...[])` return Infinity; fall back to
  // a unit domain so the axes still draw rather than producing NaN geometry.
  const lo = min ?? (finite.length ? Math.floor(Math.min(...finite) - 1) : 0);
  const hiRaw = max ?? (finite.length ? Math.ceil(Math.max(...finite) + 1) : 1);
  const hi = hiRaw === lo ? lo + 1 : hiRaw;

  const x = (i: number): number =>
    PAD.left + (i / Math.max(1, n - 1)) * (width - PAD.left - PAD.right);
  const y = (v: number): number =>
    PAD.top + (1 - (v - lo) / (hi - lo)) * (height - PAD.top - PAD.bottom);

  const yLabel = (v: number): string => (format ? format(v) : v.toFixed(dec));
  const stride = Math.max(1, Math.ceil(n / xticks));

  const tickValues = Array.from({ length: ticks + 1 }, (_, k) => lo + ((hi - lo) * k) / ticks);
  const xTickIndices: number[] = [];
  for (let i = 0; i < n; i += stride) xTickIndices.push(i);

  const showLegend = series.length > 1;

  return (
    <div className={className}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel ?? yTitle ?? 'Line chart'}
        className="block w-full"
      >
        {/* horizontal gridlines + y labels */}
        {tickValues.map((v) => (
          <g key={`y-${v}`}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(v).toFixed(1)}
              y2={y(v).toFixed(1)}
              stroke={GRID}
            />
            <text
              x={PAD.left - 6}
              y={(y(v) + 3.5).toFixed(1)}
              textAnchor="end"
              fontSize="9.5"
              fill={LABEL}
              fontFamily={MONO}
            >
              {yLabel(v)}
            </text>
          </g>
        ))}

        {/* axes */}
        <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={height - PAD.bottom} stroke={AXIS} />
        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={height - PAD.bottom}
          y2={height - PAD.bottom}
          stroke={AXIS}
        />

        {/* x labels */}
        {xTickIndices.map((i) => (
          <text
            key={`x-${i}`}
            x={x(i).toFixed(1)}
            y={height - PAD.bottom + 13}
            textAnchor="middle"
            fontSize="9.5"
            fill={LABEL}
            fontFamily={MONO}
          >
            {labels[i]}
          </text>
        ))}

        {/* axis titles */}
        {xTitle ? (
          <text
            x={(PAD.left + width - PAD.right) / 2}
            y={height - 4}
            textAnchor="middle"
            fontSize="10"
            fill={TITLE}
            fontFamily={SANS}
          >
            {xTitle}
          </text>
        ) : null}
        {yTitle ? (
          <text
            transform={`translate(11,${(PAD.top + height - PAD.bottom) / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="10"
            fill={TITLE}
            fontFamily={SANS}
          >
            {yTitle}
          </text>
        ) : null}

        {/* threshold rule */}
        {hline != null ? (
          <g>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(hline)}
              y2={y(hline)}
              stroke="var(--crit)"
              strokeDasharray="4 3"
            />
            <text
              x={width - PAD.right - 2}
              y={y(hline) - 3}
              textAnchor="end"
              fontSize="9"
              fill="var(--crit)"
              fontFamily={MONO}
            >
              {hlabel ?? ''}
            </text>
          </g>
        ) : null}

        {/* labelled reference lines — the rules under the series, the labels over them */}
        {hlines?.map((h, hi) => (
          <line
            key={`hl-${hi}`}
            x1={PAD.left}
            x2={width - PAD.right}
            y1={y(h.value).toFixed(1)}
            y2={y(h.value).toFixed(1)}
            stroke={TONE[h.tone ?? 'crit']}
            strokeDasharray={h.dashed === false ? undefined : '4 3'}
          />
        ))}

        {/* event rule */}
        {vline != null ? (
          <g>
            <line
              x1={x(vline)}
              x2={x(vline)}
              y1={PAD.top}
              y2={height - PAD.bottom}
              stroke="var(--crit)"
              strokeDasharray="3 3"
            />
            <text
              x={x(vline) - 4}
              y={PAD.top + 9}
              textAnchor="end"
              fontSize="9.5"
              fill="var(--crit)"
              fontFamily={MONO}
            >
              {vlabel ?? ''}
            </text>
          </g>
        ) : null}

        {/* series */}
        {series.map((s, si) => {
          const runs = segments(s.values);
          return (
            <g key={s.name ?? `series-${si}`}>
              {runs.map((run, ri) => {
                const points = run
                  .map((p) => `${x(p.index).toFixed(1)},${y(p.value).toFixed(1)}`)
                  .join(' ');
                const first = run[0];
                const last = run[run.length - 1];
                return (
                  <g key={`run-${ri}`}>
                    {s.area && run.length > 1 ? (
                      <polygon
                        points={`${x(first.index).toFixed(1)},${y(lo).toFixed(1)} ${points} ${x(last.index).toFixed(1)},${y(lo).toFixed(1)}`}
                        fill={s.color}
                        fillOpacity=".07"
                      />
                    ) : null}
                    <polyline
                      points={points}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={s.width ?? 1.8}
                    />
                  </g>
                );
              })}
              {s.dots
                ? s.values.map((v, i) =>
                    v == null ? null : (
                      <circle key={`dot-${i}`} cx={x(i)} cy={y(v)} r="2.2" fill={s.color} />
                    ),
                  )
                : null}
            </g>
          );
        })}
        {/* reference-line labels, haloed so they read over the series */}
        {hlines?.map((h, hi) =>
          h.label ? (
            <text
              key={`hlt-${hi}`}
              x={h.align === 'start' ? PAD.left + 4 : width - PAD.right - 2}
              y={(y(h.value) - 3).toFixed(1)}
              textAnchor={h.align === 'start' ? 'start' : 'end'}
              fontSize="9"
              fill={TONE[h.tone ?? 'crit']}
              fontFamily={MONO}
              stroke={HALO}
              strokeWidth="3"
              paintOrder="stroke"
              strokeLinejoin="round"
            >
              {h.label}
            </text>
          ) : null,
        )}

        {/* highlighted points */}
        {markers?.map((m, mi) => {
          const v = series[m.seriesIndex ?? 0]?.values[m.index];
          if (v == null) return null;
          const color = TONE[m.tone ?? 'crit'];
          return (
            <g key={`mk-${mi}`}>
              {m.title ? <title>{m.title}</title> : null}
              <circle cx={x(m.index)} cy={y(v)} r="5.5" fill="none" stroke={color} strokeWidth="1.4" />
              <circle cx={x(m.index)} cy={y(v)} r="2.6" fill={color} />
              {m.label ? (
                <text
                  x={x(m.index).toFixed(1)}
                  y={(y(v) - 9).toFixed(1)}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="600"
                  fill={color}
                  fontFamily={MONO}
                >
                  {m.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>

      {showLegend ? (
        <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-[var(--ink-secondary)]">
          {series.map((s, si) => (
            <span key={s.name ?? `legend-${si}`} className="inline-flex items-center gap-1.5">
              <i
                aria-hidden
                className="inline-block h-0 w-3 border-t-2"
                style={{ borderColor: s.color }}
              />
              {s.name ?? ''}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
